import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  TextField,
  Box,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import MapaPicker from '../components/maps/MapaPicker';

function ClienteForm() {
  const [cliente, setCliente] = useState({
    nombre_cliente: '',
    apellido_cliente: '',
    tipo_documento: 'DNI',
    numero_documento: '',
    fecha_nacimiento: '',
    genero: '',
    direccion_cliente: '',
    latitud_cliente: null,
    longitud_cliente: null,
    telefono_cliente: '',
    email_cliente: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const isEditing = Boolean(id);

  useEffect(() => {
    if (isEditing) {
      fetchCliente();
    }
  }, [id, isEditing]);

  const fetchCliente = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/api/clientes/${id}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setCliente(response.data);
    } catch (error) {
      setError('Error al cargar los datos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCliente(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await axios.put(`${config.API_BASE_URL}/api/clientes/${id}`, cliente, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      } else {
        await axios.post(`${config.API_BASE_URL}/api/clientes`, cliente, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/home/clientes');
      }, 1500);
      
    } catch (error) {
      setError(isEditing ? 'Error al actualizar el cliente' : 'Error al crear el cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/home/clientes');
  };

  const handleGoBack = () => {
    navigate(-1); // Volver a la página anterior
  };

  if (loading && isEditing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ mb: 2, color: '#1A237E' }} />
        <Typography variant="h6" color="text.secondary">
          Cargando datos del cliente...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header con botón volver */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{
              color: '#1A237E',
              '&:hover': {
                backgroundColor: 'rgba(26, 35, 126, 0.04)'
              }
            }}
          >
            Volver
          </Button>
          
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1"
              sx={{ 
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              {isEditing ? 'Editar Cliente' : 'Crear Cliente'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? 'Modifica la información del cliente' : 'Ingresa los datos del nuevo cliente'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Cliente {isEditing ? 'actualizado' : 'creado'} exitosamente. Redirigiendo...
        </Alert>
      )}

      {/* Formulario */}
      <Paper sx={{ 
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid rgba(26, 35, 126, 0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
      }}>
        {/* Header del formulario */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          color: 'white',
          p: { xs: 3, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <PersonIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold' }}>
            Información del Cliente
          </Typography>
        </Box>

        {/* Contenido del formulario */}
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Información Personal */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold', mb: 2 }}>
                  Información Personal
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  name="nombre_cliente"
                  value={cliente.nombre_cliente}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Apellido"
                  name="apellido_cliente"
                  value={cliente.apellido_cliente}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Documento</InputLabel>
                  <Select
                    name="tipo_documento"
                    value={cliente.tipo_documento}
                    onChange={handleChange}
                    label="Tipo de Documento"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="DNI">DNI</MenuItem>
                    <MenuItem value="CE">Carnet de Extranjería</MenuItem>
                    <MenuItem value="PASAPORTE">Pasaporte</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número de Documento"
                  name="numero_documento"
                  value={cliente.numero_documento}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fecha de Nacimiento"
                  name="fecha_nacimiento"
                  type="date"
                  value={cliente.fecha_nacimiento}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Género</InputLabel>
                  <Select
                    name="genero"
                    value={cliente.genero}
                    onChange={handleChange}
                    label="Género"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="M">Masculino</MenuItem>
                    <MenuItem value="F">Femenino</MenuItem>
                    <MenuItem value="O">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Información de Contacto */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold', mb: 2, mt: 2 }}>
                  Información de Contacto
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <MapaPicker
                  latitud={cliente.latitud_cliente}
                  longitud={cliente.longitud_cliente}
                  direccion={cliente.direccion_cliente}
                  onChange={({ latitud, longitud, direccion }) => {
                    setCliente(prev => ({
                      ...prev,
                      direccion_cliente: direccion,
                      latitud_cliente: latitud,
                      longitud_cliente: longitud
                    }));
                  }}
                  label="Dirección del cliente (opcional)"
                  height={280}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="telefono_cliente"
                  value={cliente.telefono_cliente}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email_cliente"
                  type="email"
                  value={cliente.email_cliente}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              {/* Botones de acción */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2, 
                  justifyContent: 'flex-end',
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid rgba(26, 35, 126, 0.1)'
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    sx={{
                      borderColor: '#1A237E',
                      color: '#1A237E',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        borderColor: '#0D47A1',
                        backgroundColor: 'rgba(26, 35, 126, 0.04)'
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                        boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: '#E0E0E0',
                        color: '#999'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {loading ? 'Guardando...' : (isEditing ? 'Actualizar Cliente' : 'Crear Cliente')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
    </Box>
  );
}

export default ClienteForm;